import React from 'react';
import { IconButton, IconButtonProps } from '@oneaudi/unified-web-components';
import { IconNamesType } from '@oneaudi/unified-web-components/dist/cjs/components/icon/icon-names.generated';
import { getImageUrl, Picture, useDynamicAltText, VehicleBasic } from '@oneaudi/vtp-shared';

import {
  ImageWrapper,
  GalleryContainer,
  LayerNavButtonContainer,
  FullScreenPictureContainer,
  StyledSlideImage,
} from '../../styles/SwipePicture.styles';

export interface FullScreenPictureProps {
  handleTouchStart: (e: React.TouchEvent) => void;
  handleTouchEnd: (e: React.TouchEvent) => void;
  handlePrev: (ismobile: boolean) => void;
  handleNext: (ismobile: boolean) => void;
  pictures: Picture[];
  carLineDescription?: string;
  imgIndex: number;
  shouldDisable: (index: number, icon: IconNamesType) => boolean;
  pictureIsFallback: boolean;
  vehicle: VehicleBasic;
}

const FullScreenPicture: React.FC<FullScreenPictureProps> = ({
  handleTouchStart,
  handleTouchEnd,
  handlePrev,
  handleNext,
  pictures,
  carLineDescription,
  imgIndex,
  shouldDisable,
  pictureIsFallback,
  vehicle,
}) => {
  const preBtnProps: IconButtonProps = {
    icon: 'back-active',
    label: '',
    size: 'sm',
    type: 'button',
    variant: 'tertiary',
    disabled: shouldDisable(imgIndex, 'back-active'),
    onClick: () => handlePrev(false),
  };

  const nextBtnProps: IconButtonProps = {
    icon: 'forward-active',
    disabled: shouldDisable(imgIndex, 'forward-active'),
    label: '',
    onClick: () => handleNext(false),
    size: 'sm',
    type: 'button',
    variant: 'tertiary',
  };

  return (
    <FullScreenPictureContainer data-testid="fullScreen-container">
      {!pictureIsFallback && (
        <LayerNavButtonContainer>
          <IconButton {...preBtnProps} aria-label="Navigate to previous image" />
        </LayerNavButtonContainer>
      )}
      <GalleryContainer onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        <ImageWrapper style={{ transform: `translateX(-${imgIndex * 100}%)` }}>
          {pictures.map((picture, indexValue) => (
            <SlideImage
              key={picture.key}
              picture={picture}
              carLineDescription={carLineDescription}
              vehicle={vehicle}
              index={indexValue}
            />
          ))}
        </ImageWrapper>
      </GalleryContainer>
      {!pictureIsFallback && (
        <LayerNavButtonContainer>
          <IconButton {...nextBtnProps} aria-label="Navigate to next image" />
        </LayerNavButtonContainer>
      )}
    </FullScreenPictureContainer>
  );
};

interface SlideImageProps {
  picture: Picture;
  carLineDescription?: string;
  vehicle: VehicleBasic;
  index: number;
}
export const SlideImage: React.FC<SlideImageProps> = ({
  picture,
  carLineDescription,
  vehicle,
  index,
}) => {
  const altForImage = useDynamicAltText(picture.key, vehicle);
  return (
    <StyledSlideImage
      key={picture.key}
      src={getImageUrl(picture.url, 1440, 1080, 'image/webp')}
      alt={altForImage}
      loading={index >= 1 ? 'lazy' : 'eager'}
      data-testid={`${carLineDescription}-${index}`}
    />
  );
};

export default FullScreenPicture;
